"use client"

import { DueDateBadge } from "../../../components/due-date-badge"
import { useTr } from "../../../lib/use-tr"
import { CRM_T } from "../crm-translations"
import { StickyNote } from "lucide-react"

import { MemberAvatar, StepIcon, TypeIcon, useStepLabel } from "./crm-bits"
import type { CrmNextStep } from "@/nango/types/apps.autoleads/autoleads/Crm"

// The one thing owed next on a company, whichever surface it comes from: a
// CRM task, a campaign's sequence step, or a reminder written on a board.
export function NextStepCell({ step }: { step: CrmNextStep | null }) {
  const tr = useTr(CRM_T)
  const stepLabel = useStepLabel()
  if (!step)
    return (
      <span className="text-muted-foreground text-sm">{tr("noNextStep")}</span>
    )

  let icon: React.ReactNode
  let label: string
  if (step.source === "crm" && step.task_type) {
    icon = <TypeIcon icon={step.task_type.icon} color={step.task_type.color} />
    label = step.label || step.task_type.label
  } else if (step.source === "auto" && step.step_type) {
    icon = <StepIcon stepType={step.step_type} />
    label = stepLabel(step.step_type)
  } else {
    icon = (
      <span
        aria-hidden
        className="bg-muted text-muted-foreground inline-flex size-6 shrink-0 items-center justify-center rounded-md"
      >
        <StickyNote className="size-3.5" />
      </span>
    )
    label = step.label || tr("sourceNote")
  }
  const context = [step.person_name, step.campaign?.name]
    .filter(Boolean)
    .join(" · ")

  return (
    <div className="flex min-w-0 items-center gap-2">
      {icon}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{label}</p>
        {context ? (
          <p className="text-muted-foreground truncate text-xs">{context}</p>
        ) : null}
      </div>
      <DueDateBadge
        due={step.due_at}
        overdue={step.overdue}
        className="shrink-0"
      />
      {/* Empty slot keeps badges aligned across rows with and without one. */}
      {step.assignee ? (
        <MemberAvatar member={step.assignee} />
      ) : (
        <span aria-hidden className="size-6 shrink-0" />
      )}
    </div>
  )
}
